import { intl } from "LocaleWrapper";
import IBAN from "iban";
import { z } from "zod";
import { ZodSchema } from "@validation/utils/schemaUtils";
import { locationSelectOptionSchema } from "view/locations/utils/location.schema";
import { RelationInput } from "__generated__/graphql";
import { ContactRoleType, LocationUserType } from "__generated__/enums";
import { shipSelectOptionSchema } from "view/ships/utils/ship.schema";
import { openingHoursSchema } from "src/zod/custom.schema";

const languageSchema = z.object({
	id: z.number(),
	code: z.string(),
	description: z.string(),
});

export const relationSelectOptionSchema = z.object({
	id: z.number(),
	name: z.string(),
	contacts: z.array(z.object({
		id: z.number(),
		name: z.string().nullish(),
		email: z.string().nullish(),
	})).nullish(),
	email: z.string().nullish(),
	applicationRelation: z.boolean().nullish(),
});

export const relationSchema = z.object({
	id: z.number().nullable(),
	customerNumber: z.number().max(999999999).nullable(),
	externalId: z.string().max(50),
	name: z.string().min(1).max(100),
	email: z.email().or(z.literal("")).optional(),
	language: languageSchema.nullable(),
	abbreviation: z.string().max(6),
	scac: z.string().max(5),
	ean: z.string().max(13),
	productGroup: z.string().nullable(),
	externalCode: z.string().max(100),
	externalCustomerNumber: z.string().max(100),
	organisation: z.string().max(200),
	telephone: z.string().max(50),
	volumePerYear: z.number().max(999999999).nullable(),
	transportFrequency: z.string().max(200),

	isCustomer: z.boolean(),
	isSupplier: z.boolean(),
	isOperator: z.boolean(),
	isShipManager: z.boolean(),
	isSkipper: z.boolean().transform(() => undefined),
	isShippingCompany: z.boolean(),
	isApplicationRelation: z.boolean(),
	isLocationRelation: z.boolean(),
	isAgent: z.boolean(),
	isConsignor: z.boolean(),
	isContainerOperator: z.boolean(),
	isBroker: z.boolean(),
	isSurveyor: z.boolean(),

	maxCargoDeviationForLetterOfProtest: z.number().max(99.99).nullable(),
	maxCargoDeviationForCallKWKW: z.number().max(99.99).nullable(),
	roundVatEachLine: z.boolean(),
	termsAndConditionsPurchase: z.string().max(200),
	termsAndConditionsSale: z.string().max(200),
	logo: z.string().nullable(),

	address: z.object({
		town: z.string().max(100),
		street: z.string().max(200),
		zipCode: z.string().max(20),
		name1: z.string().max(100),
		name2: z.string().max(100),
		country: z.string(),
	}),

	accountHolder: z.string().max(200),
	iban: z.string().transform((val, ctx) => {
		if (val && !IBAN.isValid(val)) {
			ctx.addIssue({
				code: "custom",
				fatal: true,
				message: intl.formatMessage({ id: "VALIDATION_IBAN" }),
			});

			return z.NEVER;
		}

		return val;
	}),
	bic: z.string().min(8).max(11).or(z.literal("")),
	vatReference: z.string().max(200),
	cocReference: z.string().max(200),

	workingHours: openingHoursSchema,

	contacts: z.array(z.object({
		id: z.number().nullable(),
		type: z.string().nullable(),
		contactRoleTypes: z.array(z.enum(ContactRoleType)).nullable(),
		name: z.string().min(1).max(50),
		insertion: z.string().max(10),
		surName: z.string().max(50),
		email: z.email(),
		telephone: z.string().max(25),
		defaultContact: z.boolean(),
		visibleForShip: z.boolean(),
		communicateStatusChanges: z.boolean(),
	})),

	managedShips: z.array(z.lazy(() => shipSelectOptionSchema)).nullable(),
	operatorShips: z.array(z.lazy(() => shipSelectOptionSchema)).nullable(),
	skipperShip: z.lazy(() => shipSelectOptionSchema).nullable().transform(() => undefined),

	locations: z.array(z.object({
		id: z.object({
			location: z.number().nullable(),
			customer: z.number().nullable(),
		}).nullable(),
		location: ZodSchema.requiredField(locationSelectOptionSchema.nullable()),
		type: z.enum(LocationUserType),
	})),

	products: z.array(z.object({
		id: z.number(),
		name: z.string(),
		code: z.string().nullable(),
	})).nullable(),

	linkedRelations: z.array(z.object({
		id: z.number(),
		name: z.string(),
		abbreviation: z.string().nullish(),
		email: z.string().email().or(z.literal("")).nullish(),
	})).nullable(),

	invoiceGroupLinks: z.array(z.object({
		invoiceGroupTypeId: z.number(),
		linkedRelation: relationSelectOptionSchema.nullable(),
	})),

	remarks: z.string(),

}).transform((data): RelationInput => {
	return {
		...data,
		customerNumber: data.customerNumber ? data.customerNumber.toString() : null,
		language: data.language?.code ?? "en",
		managedShips: data.managedShips?.map(ship => ship.id) ?? [],
		operatorShips: data.operatorShips?.map(ship => ship.id) ?? [],
		linkedRelations: data.linkedRelations?.map(relation => ({ id: relation.id })) ?? [],
		invoiceGroupLinks: data.invoiceGroupLinks
			.filter(l => l.linkedRelation != null)
			.map(l => ({ invoiceGroupTypeId: l.invoiceGroupTypeId, linkedRelationId: l.linkedRelation!.id })),
		workingHours: {
			...data.workingHours,
			id: data.workingHours.id ?? undefined,
		},
	};
});

export type RelationSchemaInput = z.input<typeof relationSchema>;
export type RelationSchemaOutput = z.output<typeof relationSchema>;
